import { Loader2 } from 'lucide-react'
import { useState } from 'react'

import { lookupPincode } from '@/shared/api/pincode.api'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { toast } from '@/shared/lib/toast'

export default function CustomerDetailsCard({
  form,
  onBillingChange,
  onShippingChange,
  sameAsBilling,
  onSameAsBillingChange,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer Details</CardTitle>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={sameAsBilling}
            onChange={(e) => onSameAsBillingChange(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />

          <Label className="cursor-pointer text-sm">
            Same as Billing Address
          </Label>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          <AddressSection
            title="Billing Address"
            data={form.billingAddress}
            onChange={onBillingChange}
          />

          <AddressSection
            title="Shipping Address"
            data={form.shippingAddress}
            onChange={onShippingChange}
            disabled={sameAsBilling}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function AddressSection({ title, data, onChange, disabled = false }) {
  const [isLookingUp, setIsLookingUp] = useState(false)

  const handlePincodeChange = async (e) => {
    const rawVal = e.target.value
    const pin = rawVal.replace(/\D/g, '').slice(0, 6)

    onChange('pincode', pin)

    if (pin.length === 6) {
      try {
        setIsLookingUp(true)
        const details = await lookupPincode(pin)
        if (details) {
          onChange({
            pincode: pin,
            city: details.city || data.city,
            state: details.state || data.state,
            country: details.country || data.country || 'India',
          })
          toast.success(
            `Location auto-filled: ${details.city}, ${details.state}`,
          )
        } else {
          toast.error('No location found for this pincode')
        }
      } catch (err) {
        console.error('Pincode lookup error:', err)
        toast.error('Failed to fetch location details for this pincode')
      } finally {
        setIsLookingUp(false)
      }
    }
  }

  return (
    <div className="space-y-4">
      <h4 className="text-lg font-semibold">{title}</h4>

      {/* Address Line 1 */}
      <Field
        label="Address Line 1"
        value={data.line1}
        disabled={disabled}
        onChange={(e) => onChange('line1', e.target.value)}
      />

      {/* Address Line 2 */}
      <Field
        label="Address Line 2"
        value={data.line2}
        disabled={disabled}
        onChange={(e) => onChange('line2', e.target.value)}
      />

      {/* City + State */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="City"
          value={data.city}
          disabled={disabled}
          onChange={(e) =>
            onChange(
              'city',
              e.target.value.replace(/[^a-zA-Z0-9\s.,'()/-]/g, ''),
            )
          }
        />

        <Field
          label="State"
          value={data.state}
          disabled={disabled}
          onChange={(e) =>
            onChange(
              'state',
              e.target.value.replace(/[^a-zA-Z0-9\s.,'()/-]/g, ''),
            )
          }
        />
      </div>

      {/* Pincode + Country */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Pincode
            </Label>
            {isLookingUp && (
              <span className="flex items-center gap-1 text-[11px] font-medium text-blue-600">
                <Loader2 className="h-3 w-3 animate-spin" /> Fetching...
              </span>
            )}
          </div>

          <Input
            className="h-10 rounded-lg"
            value={data.pincode}
            disabled={disabled}
            maxLength={6}
            placeholder="6-digit PIN"
            onChange={handlePincodeChange}
          />
        </div>

        <Field
          label="Country"
          value={data.country}
          disabled={disabled}
          onChange={(e) =>
            onChange('country', e.target.value.replace(/[^a-zA-Z\s]/g, ''))
          }
        />
      </div>
    </div>
  )
}

function Field({ label, value, disabled, onChange, maxLength, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>

      <Input
        className="h-10 rounded-lg"
        value={value}
        disabled={disabled}
        onChange={onChange}
        maxLength={maxLength}
        {...rest}
      />
    </div>
  )
}
